import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Text from '../Text'
import Button from '../Button'
import Checkbox from '../formControl/Checkbox'
import ContentModal from '../layout/ContentModal'
import FormFinish from '../FormFinish'
import { Row } from '../layout/LayoutAtoms'
import { SelectContact } from '../onboarding/atoms/SelectContact'

// Utils
import { useAppDispatch, useAppState } from '../../context/AppContext'
import suggestAssessorsService from '../../context/services/evaluations/suggestAssessors.service'
import {
  isAdmin,
  isMemberBuddyOfAnotherMember,
  isMemberStrictLeadOfMember,
} from '../../common/helpers/isLeadOf'

// Styles
import { mqFrom } from '../../styles/mediaQueries'

// Types
import { ICompanyMember } from '../../common/models/Company'

interface IProps {
  termNumber: number
  closeModal: () => void
}

// The SPECIFY_CLOSE_MEMBERS request: the member picks the colleagues they worked
// with most this term (select step, with their lead, buddies and admins listed
// first), reviews the picks (confirm step) and saves them as suggested assessors.
// Rendered by RequestModals, so it opens from the dashboard list and the activity
// panel alike.
const SuggestAssessorsModal: React.FC<IProps> = ({
  termNumber,
  closeModal,
}) => {
  const { t } = useTranslation()
  const appDispatch = useAppDispatch()
  const { company, user, userAuthToken } = useAppState()
  const authUser = user?.asCompanyMember

  const [step, setStep] = useState<'select' | 'confirm'>('select')
  const [selectedMembers, setSelectedMembers] = useState<ICompanyMember[]>([])

  const toggleMember = (employee: ICompanyMember) =>
    setSelectedMembers((value) =>
      value.some((member) => member._id === employee._id)
        ? value.filter((member) => member._id !== employee._id)
        : [...value, employee]
    )

  const saveSuggestions = async () => {
    try {
      await suggestAssessorsService(
        authUser?._id || '',
        termNumber,
        selectedMembers.map((member) => member._id),
        userAuthToken || ''
      )
      appDispatch({ type: 'updateShouldLoadRequests', payload: true })
    } catch (e) {
      //
    }
  }

  return (
    <ContentModal
      title={t('requests.suggestTitle')}
      subtitle={t('requests.suggestText')}
      closeModal={closeModal}
      peoplePickerVersion={
        step === 'confirm'
          ? undefined
          : {
              members: company?.members || [],
              multipleSelect: true,
              notIncludedMembers: [authUser?._id],
              selectedMemberIds: selectedMembers.map((member) => member._id),
              action: toggleMember,
            }
      }
      bottomComponents={({ isFiltering }) => (
        <>
          {step === 'select' ? (
            authUser &&
            !isFiltering && (
              <VariableContacts>
                <Text
                  textStyle="labelSmallCaps"
                  color="text72"
                  marginTop={12}
                  marginBottom={12}
                >
                  {t('termEvaluationsProgress.relevantPeople')}
                </Text>
                {company?.members
                  .filter(
                    (mem) =>
                      authUser._id !== mem._id &&
                      (isMemberBuddyOfAnotherMember(mem, authUser) ||
                        isMemberStrictLeadOfMember(mem, authUser) ||
                        isAdmin(mem))
                  )
                  .map((mem) => (
                    <SelectContact
                      key={`contact-type-${mem._id}`}
                      contactMember={mem}
                      title={mem.name}
                      action={() => toggleMember(mem)}
                      subtitle={
                        isMemberStrictLeadOfMember(mem, authUser)
                          ? t('general.users.yourLead')
                          : isMemberBuddyOfAnotherMember(mem, authUser)
                          ? t('general.users.yourBuddy')
                          : t('general.users.yourAdmin')
                      }
                      actionElement={
                        <StyledCheckbox
                          color="primary"
                          checked={selectedMembers.some(
                            (s) => s._id === mem._id
                          )}
                        />
                      }
                    />
                  ))}
              </VariableContacts>
            )
          ) : (
            <>
              <Text textStyle="title" marginBottom={4}>
                {t('requests.suggestions')}
              </Text>
              <Text textStyle="bodySmall" marginBottom={24}>
                {t('requests.suggestionsText')}
              </Text>
              {selectedMembers.map((member) => (
                <SelectContact
                  key={`selectedMember-${member._id}`}
                  contactMember={member}
                  title={member.name}
                  disabled
                />
              ))}
              <Row>
                <StyledButton
                  caption={t('general.actions.edit')}
                  icon="Pencil"
                  colorVariant="secondary"
                  onClick={() => setStep('select')}
                />
                <StyledButton
                  caption={t('general.actions.confirm')}
                  icon="Check"
                  colorVariant="primary"
                  onClick={() => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    saveSuggestions()
                    closeModal()
                  }}
                />
              </Row>
            </>
          )}
          {selectedMembers.length > 0 && step === 'select' && (
            <StyledFormFinish
              buttonText={t('addEditUser.nextStep')}
              buttonIcon="ArrowRight"
              onClick={() => setStep('confirm')}
            />
          )}
        </>
      )}
    />
  )
}

export default SuggestAssessorsModal

const VariableContacts = styled.div`
  margin-bottom: 24px;
`

const StyledCheckbox = styled(Checkbox)`
  margin-left: auto;
`

const StyledFormFinish = styled(FormFinish)`
  ${mqFrom.M`
     bottom: 88px;
  `}
`

const StyledButton = styled(Button)`
  margin-top: 16px;

  ${mqFrom.M`
    width: calc(50% - 6px);
  `}
`
